import React, {useState, useRef, useMemo, useCallback, useEffect} from "react";
import {Link} from "react-router-dom";
import {Breadcrumb, Button, Table, Skeleton, Tooltip, message} from "antd";
import {DashboardWrapper} from "../../../../layouts/dashboard-wrapper";
import {
  StyledEmailAutomationsHeader,
  StyledEmptyStateBox,
  StyledEmailAutomationsTableWrapper,
  EmailAutomationsActionsDropdownStyles,
} from "../automations.style";
import {CustomModal} from "../../../../components/modal";
import {StyledModalContentWrapper} from "../../../../components/calls/upcoming-calls/call-card/call-card.style";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {DEFAULT_BODY} from "./_components/constants";
import {
  getEditFormValues,
  getEditFormValuesFromApi,
  getDuplicateFormValues,
  resolveOfferingIdsForMultiselect,
} from "./_components/editFormData";
import {
  createAutomationSchema,
  draftAutomationSchema,
  type CreateAutomationFormValues,
} from "./_components/schema";
import type {EmailAutomationRow} from "./_components/types";
import {normalizeEmailAutomation} from "./_components/types";
import {
  CreateAutomationDrawer,
  type DrawerView,
} from "./_components/CreateAutomationDrawer";
import {ViewAutomationDrawer} from "./_components/ViewAutomationDrawer";
import ReactQuill from "react-quill";
import {useEmailAutomationsColumns} from "./_components/useEmailAutomationsColumns";
import {insertEmailAutomationShortCode} from "./_components/insert-short-code";
import {
  useCreateEmailAutomation,
  useUpdateEmailAutomation,
  useDeleteEmailAutomation,
  useGetEmailAutomations,
  useGetEmailAutomationsByTrigger,
} from "../../../../react-query-hooks/email-automation";
import {
  MAX_EMAIL_AUTOMATIONS_PER_TRIGGER,
  EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE,
  EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE,
  pickDefaultTriggerForCreate,
  countForFormTriggerFromSplitLists,
} from "./_components/email-automation-limits";
import {transformFormToApiPayload} from "./_components/api-types";
import {useGetOfferings} from "@/react-query-hooks/offerings";
import {useEmailAutomationSaveErrorUI} from "./_components/useEmailAutomationSaveErrorUI";

export function EmailAutomationsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const emailAutomationsQuery = useGetEmailAutomations(currentPage, pageSize);
  const createEmailAutomation = useCreateEmailAutomation();
  const updateEmailAutomation = useUpdateEmailAutomation();
  const deleteEmailAutomation = useDeleteEmailAutomation();
  const offeringsQuery = useGetOfferings();
  const afterPurchaseQuery = useGetEmailAutomationsByTrigger("after-purchase");
  const abandonedPurchaseQuery = useGetEmailAutomationsByTrigger("abandoned-purchase");
  const afterPurchaseItems = afterPurchaseQuery.data ?? [];
  const abandonedPurchaseItems = abandonedPurchaseQuery.data ?? [];
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState<EmailAutomationRow | null>(
    null
  );
  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EmailAutomationRow | null>(null);
  const [isViewDrawerVisible, setIsViewDrawerVisible] = useState(false);
  const [currentDrawerView, setCurrentDrawerView] = useState<DrawerView>("form");
  const [shortCodeTarget, setShortCodeTarget] = useState<"subject" | "body" | null>(null);

  const subjectInputRef = useRef<HTMLInputElement | null>(
    null
  ) as React.MutableRefObject<HTMLInputElement | null>;
  const quillRef = useRef<ReactQuill>(null);
  const subjectCursorPos = useRef<number | null>(null);
  const bodyCursorPos = useRef<number | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: {errors},
  } = useForm<CreateAutomationFormValues>({
    resolver: yupResolver(createAutomationSchema),
    mode: "onSubmit",
    defaultValues: {
      automationName: "",
      subject: "Thank you for your purchase, {Customer_name}",
      body: DEFAULT_BODY,
      addCustomButton: false,
      buttonText: "",
      buttonLink: "",
      trigger: "after_purchase",
      offerings: [],
      sendTiming: "immediately",
      delayValue: 5,
      delayUnit: "minutes",
    },
  });

  const rawItems = emailAutomationsQuery.data?.items ?? [];
  const data = rawItems.map(normalizeEmailAutomation);
  const total = emailAutomationsQuery.data?.total ?? 0;
  const isEmpty = data.length === 0;

  const defaultFormValues: CreateAutomationFormValues = {
    automationName: "",
    subject: "Thank you for your purchase, {Customer_name}",
    body: DEFAULT_BODY,
    addCustomButton: false,
    buttonText: "",
    buttonLink: "",
    trigger: "after_purchase",
    offerings: [],
    sendTiming: "immediately",
    delayValue: 5,
    delayUnit: "minutes",
  };

  const triggerQuotaBlocked = useMemo(
    () => ({
      after_purchase: afterPurchaseItems.length >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER,
      abandoned_purchase:
        abandonedPurchaseItems.length >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER,
    }),
    [afterPurchaseItems.length, abandonedPurchaseItems.length]
  );

  const triggerCountsReady =
    !afterPurchaseQuery.isLoading && !abandonedPurchaseQuery.isLoading;

  const bothTriggersAtLimit =
    triggerCountsReady &&
    triggerQuotaBlocked.after_purchase &&
    triggerQuotaBlocked.abandoned_purchase;

  const duplicateDisabled = useCallback(
    (record: EmailAutomationRow) => {
      const len =
        record.trigger === "after-purchase"
          ? afterPurchaseItems.length
          : abandonedPurchaseItems.length;
      return len >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER;
    },
    [afterPurchaseItems.length, abandonedPurchaseItems.length]
  );

  const availableOfferings = offeringsQuery.data ?? [];

  const resetForm = () => {
    reset(defaultFormValues);
    setCurrentDrawerView("form");
    setShortCodeTarget(null);
    setEditingRecord(null);
  };

  const closeCreateDrawer = () => {
    setIsCreateDrawerVisible(false);
    resetForm();
  };

  const {handleSaveError, ScheduleConflictModal} =
    useEmailAutomationSaveErrorUI(closeCreateDrawer);

  const openDrawerForEdit = (record: EmailAutomationRow) => {
    setEditingRecord(record);
    const apiItem = rawItems.find((item) => String(item.id) === record.key);
    if (apiItem) {
      reset(getEditFormValuesFromApi(apiItem, availableOfferings));
    } else {
      reset(getEditFormValues(record));
    }
    setIsCreateDrawerVisible(true);
  };

  useEffect(() => {
    if (!isCreateDrawerVisible || !editingRecord) return;
    const opts = offeringsQuery.data;
    if (!opts?.length) return;
    const apiItem = rawItems.find((item) => String(item.id) === editingRecord.key);
    if (!apiItem) return;
    const pivotIds = (apiItem.settings?.pivot ?? []).map((p) => p.offeringId);
    if (pivotIds.length === 0) return;
    setValue("offerings", resolveOfferingIdsForMultiselect(pivotIds, opts), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [isCreateDrawerVisible, editingRecord, rawItems, offeringsQuery.data, setValue]);

  const openDrawerForDuplicate = (record: EmailAutomationRow) => {
    setEditingRecord(null);
    reset(getDuplicateFormValues(record));
    setIsCreateDrawerVisible(true);
  };

  const insertShortCode = (code: string) =>
    insertEmailAutomationShortCode(code, {
      target: shortCodeTarget,
      getSubject: () => getValues("subject") || "",
      setValue,
      subjectInputRef,
      subjectCursorPos,
      bodyCursorPos,
      quillRef,
      onAfterInsert: () => {
        setCurrentDrawerView("form");
        setShortCodeTarget(null);
      },
    });

  const openNewAutomationInDrawer = () => {
    setEditingRecord(null);
    reset({
      ...defaultFormValues,
      trigger: pickDefaultTriggerForCreate(triggerQuotaBlocked),
    });
    setIsCreateDrawerVisible(true);
  };

  const onCreateAutomation = (values: CreateAutomationFormValues) => {
    const payload = transformFormToApiPayload(values, false, availableOfferings);

    if (editingRecord) {
      updateEmailAutomation.mutate(
        {
          id: editingRecord.key,
          payload,
        },
        {
          onSuccess: () => {
            setIsCreateDrawerVisible(false);
            resetForm();
          },
          onError: handleSaveError,
        }
      );
    } else {
      const existingCount = countForFormTriggerFromSplitLists(
        values.trigger,
        afterPurchaseItems,
        abandonedPurchaseItems
      );
      if (existingCount >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER) {
        message.error(EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE);
        return;
      }
      createEmailAutomation.mutate(payload, {
        onSuccess: () => {
          setIsCreateDrawerVisible(false);
          resetForm();
        },
        onError: handleSaveError,
      });
    }
  };

  const onSaveDraft = (values: CreateAutomationFormValues) => {
    const payload = transformFormToApiPayload(values, true, availableOfferings);

    if (editingRecord) {
      updateEmailAutomation.mutate(
        {
          id: editingRecord.key,
          payload,
        },
        {
          onSuccess: () => {
            setIsCreateDrawerVisible(false);
            resetForm();
          },
          onError: handleSaveError,
        }
      );
    } else {
      const existingCount = countForFormTriggerFromSplitLists(
        values.trigger,
        afterPurchaseItems,
        abandonedPurchaseItems
      );
      if (existingCount >= MAX_EMAIL_AUTOMATIONS_PER_TRIGGER) {
        message.error(EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE);
        return;
      }
      createEmailAutomation.mutate(payload, {
        onSuccess: () => {
          setIsCreateDrawerVisible(false);
          resetForm();
        },
        onError: handleSaveError,
      });
    }
  };

  const onSaveDraftClick = async () => {
    const values = getValues();
    try {
      const validated = await draftAutomationSchema.validate(values, {abortEarly: false});
      clearErrors();
      onSaveDraft(validated as CreateAutomationFormValues);
    } catch (err: unknown) {
      if (err instanceof Error && "inner" in err) {
        const yupErr = err as {inner: {path: string; message: string}[]};
        clearErrors();
        yupErr.inner.forEach(({path, message: msg}) => {
          setError(path as keyof CreateAutomationFormValues, {message: msg});
        });
      }
    }
  };

  const showDeleteModal = (record: EmailAutomationRow) => {
    setSelectedAutomation(record);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = () => {
    if (!selectedAutomation) return;
    deleteEmailAutomation.mutate(selectedAutomation.key, {
      onSuccess: () => {
        setIsDeleteModalVisible(false);
        setSelectedAutomation(null);
        setIsViewDrawerVisible(false);
      },
    });
  };

  const handleMenuClick = (key: string, record: EmailAutomationRow) => {
    if (key === "delete") {
      showDeleteModal(record);
    } else if (key === "view") {
      setSelectedAutomation(record);
      setIsViewDrawerVisible(true);
    } else if (key === "edit") {
      openDrawerForEdit(record);
    } else if (key === "duplicate") {
      if (duplicateDisabled(record)) {
        message.error(EMAIL_AUTOMATION_TRIGGER_LIMIT_REACHED_MESSAGE);
        return;
      }
      openDrawerForDuplicate(record);
    }
  };

  const columns = useEmailAutomationsColumns(handleMenuClick);

  return (
    <DashboardWrapper pageTitle="Email automations">
      <EmailAutomationsActionsDropdownStyles />
      <StyledEmailAutomationsHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/automations">Automations</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Email automations</Breadcrumb.Item>
        </Breadcrumb>
        {!isEmpty && (
          <Tooltip
            title={
              bothTriggersAtLimit
                ? EMAIL_AUTOMATIONS_ALL_TRIGGERS_AT_LIMIT_MESSAGE
                : undefined
            }
          >
            <span style={{display: "inline-block"}}>
              <Button
                type="primary"
                style={{borderRadius: "12px"}}
                size="small"
                disabled={bothTriggersAtLimit}
                onClick={openNewAutomationInDrawer}
              >
                Create an email automation
              </Button>
            </span>
          </Tooltip>
        )}
      </StyledEmailAutomationsHeader>

      {emailAutomationsQuery.isLoading ? (
        <StyledEmailAutomationsTableWrapper>
          <Table
            columns={columns.map((col) => ({
              ...col,
              render: () => (
                <Skeleton.Input
                  active
                  size="small"
                  style={{width: "100%", height: "20px", borderRadius: "4px"}}
                />
              ),
            }))}
            dataSource={[...Array(5)].map((_, i) => ({key: `skeleton-${i}`})) as any}
            pagination={false}
            scroll={{x: 720}}
          />
        </StyledEmailAutomationsTableWrapper>
      ) : isEmpty ? (
        <StyledEmptyStateBox>
          <p className="empty-state-text">
            You do not have any email automations at the moment
          </p>
          <Button
            type="primary"
            style={{borderRadius: "12px"}}
            size="small"
            onClick={openNewAutomationInDrawer}
          >
            Create an email automation
          </Button>
        </StyledEmptyStateBox>
      ) : (
        <StyledEmailAutomationsTableWrapper>
          <Table<EmailAutomationRow>
            columns={columns}
            dataSource={data}
            scroll={{x: 720}}
            pagination={{
              position: ["bottomLeft"],
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: false,
              onChange: (page) => {
                setCurrentPage(page);
              },
              itemRender: (page, type, originalElement) => {
                if (type === "prev") return <Button size="small">Previous</Button>;
                if (type === "next") return <Button size="small">Next</Button>;
                if (type === "page") {
                  return (
                    <Button
                      size="small"
                      type={page === currentPage ? "primary" : "default"}
                    >
                      {page}
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
          />
        </StyledEmailAutomationsTableWrapper>
      )}

      <CustomModal
        title="Delete automation"
        visibleState={isDeleteModalVisible}
        okFunc={handleDelete}
        cancelFunc={() => setIsDeleteModalVisible(false)}
        centered
        width={500}
        footerContent={[
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleteEmailAutomation.isPending}
            onClick={handleDelete}
            size="small"
          >
            Delete automation
          </Button>,
          <Button
            key="cancel"
            type="default"
            onClick={() => setIsDeleteModalVisible(false)}
            size="small"
          >
            Cancel
          </Button>,
        ]}
      >
        <StyledModalContentWrapper>
          <p>
            This action cannot be undone. Emails will stop sending for any assigned
            offerings.
          </p>
        </StyledModalContentWrapper>
      </CustomModal>

      {ScheduleConflictModal}

      <CreateAutomationDrawer
        visible={isCreateDrawerVisible}
        isEditMode={editingRecord !== null}
        onClose={() => {
          setIsCreateDrawerVisible(false);
          resetForm();
        }}
        currentView={currentDrawerView}
        onViewChange={setCurrentDrawerView}
        formProps={{
          control,
          errors,
          watch,
          setValue,
          clearErrors,
          offeringOptions: availableOfferings,
          isOfferingsLoading: offeringsQuery.isLoading,
          subjectInputRef,
          quillRef,
          subjectCursorPos,
          bodyCursorPos,
          onOpenShortCodes: (target) => {
            setShortCodeTarget(target);
            setCurrentDrawerView("shortcodes");
          },
          onOpenPreview: () => setCurrentDrawerView("preview"),
          triggerQuotaBlocked: editingRecord ? undefined : triggerQuotaBlocked,
        }}
        handleSubmit={handleSubmit}
        watch={watch}
        onSubmit={onCreateAutomation}
        onSaveDraft={onSaveDraft}
        onSaveDraftClick={onSaveDraftClick}
        onInsertShortCode={insertShortCode}
        isSubmitting={createEmailAutomation.isLoading || updateEmailAutomation.isLoading}
        onDeleteAndClose={() => {
          setIsCreateDrawerVisible(false);
          resetForm();
        }}
      />

      <ViewAutomationDrawer
        visible={isViewDrawerVisible}
        onClose={() => setIsViewDrawerVisible(false)}
        automation={selectedAutomation}
        onEdit={() => {
          if (!selectedAutomation) return;
          setIsViewDrawerVisible(false);
          openDrawerForEdit(selectedAutomation);
        }}
        onDelete={() => {
          setIsDeleteModalVisible(true);
        }}
      />
    </DashboardWrapper>
  );
}
